/**
 * Client for the /users/tier-recommendation API.
 *
 * Issue #5554 — usage-based tier recommendation at conversion.
 *
 * The endpoint reads the user's real Full/Deep trial usage and recommends
 * either 'starter' or 'plus'. Thresholds are sourced from the Starter plan
 * row in the plans table on the backend — NOT hardcoded here.
 */
import ApiService, { type ApiResponse } from './api-services';

/**
 * Recommended plan tag — matches plans.tag in the DB.
 * 'starter' = Starter plan; 'plus' = Pro plan.
 * Never use display names ('Starter', 'Pro') as identifiers.
 */
export type RecommendedPlanTag = 'starter' | 'plus';

export type TierRecommendation = {
  /** Canonical plan tag for the recommended tier. */
  recommended_tag: RecommendedPlanTag;
  /** Highest Full-mode events in any single calendar day during the trial. */
  peak_daily_full: number;
  /** Highest Deep-mode events in any single calendar day during the trial. */
  peak_daily_deep: number;
  /** Full+Deep tier events since the start of the current calendar month. */
  monthly_advanced_total: number;
  /** True when any usage metric exceeded Starter's DB-sourced limits. */
  exceeded_starter: boolean;
  /**
   * True when the user has at least one Full/Deep tier event this month.
   * When false the recommendation carries no signal — the personalised badge
   * must be suppressed (a zero-usage user has no "trial usage" to base it on).
   */
  has_trial_usage: boolean;
};

/**
 * Pure decision: should the personalised recommendation badge render on the
 * card for ``planId``?
 *
 * Extracted from PricingV2Section's JSX (#5563) so the section-level
 * badge→card mapping is locked by a behavioural unit test rather than a
 * source-string grep — a fence that a ``===``→``!==`` mutation would slip past.
 *
 * Rules (must match the badge contract from #5554):
 *   - No recommendation (unauthenticated / fetch failed) → never show.
 *   - Zero trial usage (``has_trial_usage === false``) → never show. The badge
 *     claims "based on your trial usage", which would be misleading with none.
 *   - Otherwise show only on the card whose tag equals ``recommended_tag``.
 *     ``planId`` carries ``plans.tag`` verbatim; the comparison lowercases it
 *     because the recommended tag is canonical lowercase (CLAUDE.md plan
 *     catalog — ``plan.id`` may be Title-case on some surfaces).
 *
 * @param recommendation - The fetched recommendation, or null when absent.
 * @param planId - The card's plan identifier (``plan.id``, carries plans.tag).
 */
export function shouldShowRecommendationBadge(
  recommendation: TierRecommendation | null,
  planId: string
): boolean {
  if (recommendation == null) return false;
  if (!recommendation.has_trial_usage) return false;
  return recommendation.recommended_tag === planId.toLowerCase();
}

class TierRecommendationService extends ApiService {
  constructor() {
    super('/users');
  }

  /**
   * Fetch the usage-based tier recommendation for the current authenticated user.
   *
   * Resolves to the standard ``ApiResp`` envelope. ``data`` is null for
   * unauthenticated users (the backend returns status='errors', code=401 —
   * callers should check ``status`` before reading ``data``).
   */
  getRecommendation(): ApiResponse<TierRecommendation | null> {
    return this.getData('/tier-recommendation');
  }
}

export default new TierRecommendationService();
